import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useFrameManager } from '@bcraft-frontend-lib/slots'
import block from 'bem-cn'
import dayjs from 'dayjs'

import { ROUTE_URLS } from 'router/routes'

import { getCurrencySymbol } from 'shared/constants/currencies/getCurrencySymbol'
import { BonusPromotionStatus } from 'shared/models/Promotion'

import { SlotsGameType } from 'modules/SlotsRoot/types/interfaces/Slots/Slots'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectBonusPromotionsPolling } from 'features/bonusSystem/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import TopBannerHoc from 'components/HOC/TopBannerHoc/TopBannerHoc'

import Button from 'components/UIKit/Button/Button'

import {
  activePromotionStatuses,
  bonusPromotionStatusWeights,
  takeYourBonusStatuses,
} from './BonusPromotionNotification.utils'

import './BonusPromotionNotification.scss'

const b = block('bonus-promotion-notification')

const MOCK_BONUS_NOTIFICATION =
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('mockBonusNotification')

const MOCK_BONUS_MESSAGE =
  'Play your favorite slots now to unlock exclusive bonus reward 100% up to 100€ lorem ipsum dolor sit amet'

const BonusPromotionNotification = () => {
  const history = useHistory()
  const locale = useSelector(selectLocaleDict, shallowEqual).bonuses
  const { count: framesCount } = useFrameManager()
  const promotions = useSelector(selectBonusPromotionsPolling, shallowEqual)
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const [isShowBonusNotification, setIsShowBonusNotification] = useState<boolean>(MOCK_BONUS_NOTIFICATION)

  useEffect(() => {
    if (MOCK_BONUS_NOTIFICATION) {
      return () => {}
    }

    const showHideTimer = setTimeout(() => {
      if (framesCount === 0) setIsShowBonusNotification(true)
    }, 2000)

    return () => {
      clearTimeout(showHideTimer)
    }
  }, [framesCount])

  const activePromotion = useMemo(
    () => promotions.find(promotion => activePromotionStatuses.indexOf(promotion.status) !== -1),
    [promotions],
  )

  const takeBonus = useMemo(
    () => promotions.find(promotion => takeYourBonusStatuses.indexOf(promotion.status) !== -1),
    [promotions],
  )

  const activePromotionWeight = useMemo(
    () => (activePromotion ? bonusPromotionStatusWeights[activePromotion.status] : 0),
    [activePromotion],
  )

  const takeBonusWight = useMemo(
    () => (takeBonus ? bonusPromotionStatusWeights[takeBonus.status] : 0),
    [takeBonus],
  )

  const currentDate = dayjs()

  const allPromotionsIsExpired = useMemo(
    () => promotions?.every(promo => currentDate.diff(promo.expirationDate) >= 0),

    [promotions, currentDate],
  )
  const handleCloseNotification = useCallback(() => {
    setIsShowBonusNotification(false)
  }, [setIsShowBonusNotification])

  const handleButtonClick = useCallback(
    (route: string) => {
      history.push(route)
      handleCloseNotification()
    },
    [handleCloseNotification, history],
  )

  if (MOCK_BONUS_NOTIFICATION) {
    return (
      <TopBannerHoc
        allPromotionsIsExpired={false}
        isShowNotification={isShowBonusNotification}
        onCloseBanner={handleCloseNotification}
        needAuth={false}>
        <div className={b({ isWager: true })}>
          <div className={b('text')}>
            <span>{MOCK_BONUS_MESSAGE}</span>
          </div>
          <div className={b('button-wrapper')}>
            <Button
              text={locale.openGames}
              onClick={() => handleButtonClick(ROUTE_URLS.SLOTS(SlotsGameType.wagering))}
            />
          </div>
        </div>
      </TopBannerHoc>
    )
  }

  if (!isAuth) return null

  if (activePromotion && takeBonusWight < activePromotionWeight) {
    return activePromotion!.freeSpinsCount > 0 ? (
      <TopBannerHoc
        allPromotionsIsExpired={allPromotionsIsExpired}
        isShowNotification={isShowBonusNotification}
        onCloseBanner={handleCloseNotification}
        needAuth>
        <div className={b({ isFreeSpins: true })}>
          <div className={b('text')}>{locale.clickForPlayFreeSpins}</div>

          <div className={b('button-wrapper')}>
            <Button
              text={locale.openGames}
              onClick={() => handleButtonClick(ROUTE_URLS.SLOTS(SlotsGameType.freeSpin))}
            />
          </div>
        </div>
      </TopBannerHoc>
    ) : (
      <TopBannerHoc
        allPromotionsIsExpired={allPromotionsIsExpired}
        isShowNotification={isShowBonusNotification}
        onCloseBanner={handleCloseNotification}
        needAuth>
        <div className={b({ isWager: true })}>
          <div className={b('text')}>
            {/* <span>{locale.playFor}</span>
            <span>
              {Number(activePromotion.wager - activePromotion.wagerComplete).toFixed(2)} {activePromotion.wagerCurrency}
            </span>
            <span>{locale.toGet}</span>
            <span>
              {Number(activePromotion.promotionGrandAmount.toFixed(2))} {activePromotion.wagerCurrency}
            </span>
            <span>{locale.bonus.toLowerCase()}</span> */}
            <span>
              {locale.wagerText} {Number(activePromotion.promotionGrandAmount.toFixed(2))}{' '}
              {getCurrencySymbol(activePromotion.promotionGrandAmountCurrency)}
            </span>
          </div>

          <div className={b('button-wrapper')}>
            <Button
              text={locale.openGames}
              onClick={() => handleButtonClick(ROUTE_URLS.SLOTS(SlotsGameType.wagering))}
            />
          </div>
        </div>
      </TopBannerHoc>
    )
  }

  if (takeBonus) {
    return (
      <TopBannerHoc
        allPromotionsIsExpired={allPromotionsIsExpired}
        isShowNotification={isShowBonusNotification}
        onCloseBanner={handleCloseNotification}
        needAuth>
        <div
          className={b({ isTakeBonus: true })}
          onClick={() => history.push(ROUTE_URLS.PROFILE_PROMOTIONS_STATUS(BonusPromotionStatus.completed))}>
          <div className={b('text')}>{locale.takeYourBonus}</div>
          <div className={b('button-wrapper')}>
            <Button
              text={locale.bonuses}
              onClick={() => handleButtonClick(ROUTE_URLS.PROFILE_PROMOTIONS_STATUS(BonusPromotionStatus.completed))}
            />
          </div>
        </div>
      </TopBannerHoc>
    )
  }

  return <></>
}

export default BonusPromotionNotification
